import { createFileRoute, Link } from '@tanstack/react-router'
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import { DollarSign, Clock, RefreshCcw, AlertCircle, CheckCircle, CreditCard, ArrowLeft, Mail } from "lucide-react"
import { useTranslation } from "@/lib/i18n"

import { createLLMOMeta, createWebPageSchema, createBreadcrumbSchema } from '@/lib/seo'

export const Route = createFileRoute('/refund-policy')({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: 'Refund Policy - Zapify | 14-Day Money-Back Guarantee',
        description: 'Understand our refund policies for Zapify subscriptions and services. 14-day money-back guarantee for all new subscriptions.',
        url: 'https://zapify.pro/refund-policy',
        publishedTime: '2024-12-28T00:00:00Z',
      }),
      { property: "og:type", content: "article" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/refund-policy" }
    ],
    scripts: [
      createWebPageSchema({
        name: 'Refund Policy - Zapify',
        description: 'Understand our refund policies for Zapify subscriptions and services.',
        url: 'https://zapify.pro/refund-policy',
      }),
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: 'Home', item: 'https://zapify.pro' },
          { position: 2, name: 'Refund Policy', item: 'https://zapify.pro/refund-policy' },
        ],
      }),
    ],
  }),
  component: RefundPolicyPage,
})

export function RefundPolicyPage() {
  const { translations: t } = useTranslation();
  return (
    <main className="min-h-screen bg-white dark:bg-black py-20 px-4 md:px-8 transition-colors">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <Badge variant="outline" className="px-4 py-1 border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 mb-2">
            {t.legal.lastUpdated}: December 28, 2024
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-600 to-teal-600 dark:from-green-400 dark:to-teal-400">
            {t.legal.refundPolicy}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
            We want you to be completely satisfied with Zapify. Our refund policy
            is designed to be fair, transparent, and easy to understand.
          </p>
        </div>

        <Card className="border-gray-200 dark:border-neutral-800 shadow-lg bg-white dark:bg-neutral-900">
          <CardContent className="p-8 md:p-12 space-y-12">
            <div className="prose prose-gray dark:prose-invert max-w-none space-y-12">
              
              {/* Introduction */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    1. 14-Day Money-Back Guarantee
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed ml-11">
                  <p>
                    We offer a no-questions-asked 14-day money-back guarantee for
                    all new subscriptions. If you're not satisfied with our
                    service for any reason within the first 14 days, simply
                    contact us for a full refund.
                  </p>
                  <p>
                    This guarantee applies to your first billing cycle only and
                    is available to all new customers.
                  </p>
                </div>
              </section>

              <Separator className="bg-gray-100 dark:bg-neutral-800" />

              {/* Eligibility */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    2. Refund Eligibility
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>To be eligible for a refund, your request must meet the following criteria:</p>
                  <ul className="grid gap-3 list-disc pl-4">
                    <li>
                      Request is made within 14 days of the initial purchase
                    </li>
                    <li>
                      The account has not violated our Terms of Service
                    </li>
                    <li>
                      You have not previously received a refund for the same service
                    </li>
                    <li>
                        The subscription was purchased directly through our website
                    </li>
                  </ul>
                </div>
              </section>

              {/* Cancellation */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    3. Cancellation Policy
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    You can cancel your subscription at any time through your account settings. Once cancelled:
                  </p>
                  <p>
                    Subscription fees pay for access to Zapify during the
                    applicable billing period. If you remain subscribed but do
                    not use the service, you are still responsible for the
                    subscription fee, and we are not liable or obligated to
                    provide a refund or credit for that unused period.
                  </p>
                  <ul className="grid gap-3 list-disc pl-4">
                    <li>
                      Billing will stop immediately (no future charges)
                    </li>
                    <li>
                      You will retain access to premium features until the end of your current billing period
                    </li>
                    <li>
                      No partial refunds are issued for unused time in the
                      current billing cycle after the 14-day window
                    </li>
                  </ul>
                </div>
              </section>

              <Separator className="bg-gray-100 dark:bg-neutral-800" />

              {/* Refund Process */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <RefreshCcw className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    4. Refund Process
                  </h2>
                </div>
                <div className="space-y-6 ml-11">
                  <p className="text-gray-700 dark:text-gray-300">
                    Once we receive your refund request, here's what happens:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-900">
                      <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                      <h3 className="font-semibold text-blue-800 dark:text-blue-300">
                        Review Period
                      </h3>
                      <p className="text-sm text-blue-700 dark:text-blue-400">1-3 business days</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg border border-yellow-200 dark:border-yellow-900">
                      <CheckCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400 mx-auto mb-2" />
                      <h3 className="font-semibold text-yellow-800 dark:text-yellow-300">
                        Approval
                      </h3>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400">
                        Same day notification
                      </p>
                    </div>
                    <div className="text-center p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-900">
                      <DollarSign className="w-8 h-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                      <h3 className="font-semibold text-green-800 dark:text-green-300">
                        Processing
                      </h3>
                      <p className="text-sm text-green-700 dark:text-green-400">
                        5-10 business days
                      </p>
                    </div>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                    <p className="text-blue-800 dark:text-blue-300 text-sm">
                      <strong>Note:</strong> Processing times may vary depending
                      on your payment method and financial institution. Credit
                      card refunds typically appear within 5-7 business days,
                      while PayPal refunds are usually instant.
                    </p>
                  </div>
                </div>
              </section>

              {/* Special Circumstances */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    5. Special Circumstances
                  </h2>
                </div>
                <div className="space-y-4 ml-11">
                  <div className="border border-gray-200 dark:border-neutral-800 rounded-lg p-4 bg-gray-50 dark:bg-neutral-800/50">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Partial Refunds
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm">
                      In some cases, we may offer partial refunds for services
                      that have been partially used or for specific features
                      that didn't meet expectations.
                    </p>
                  </div>
                  <div className="border border-gray-200 dark:border-neutral-800 rounded-lg p-4 bg-gray-50 dark:bg-neutral-800/50">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Service Credits
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm">
                      As an alternative to refunds, we may offer service credits
                      that can be applied to future purchases or subscription
                      periods.
                    </p>
                  </div>
                  <div className="border border-gray-200 dark:border-neutral-800 rounded-lg p-4 bg-gray-50 dark:bg-neutral-800/50">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Technical Issues
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm">
                      If you experience technical difficulties, we'll work with
                      you to resolve the issue before considering a refund. Most
                      technical problems can be resolved quickly.
                    </p>
                  </div>
                </div>
              </section>

              {/* Payment Methods */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <CreditCard className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    6. Refund Methods
                  </h2>
                </div>
                <div className="space-y-3 text-gray-700 dark:text-gray-300 leading-relaxed ml-11">
                  <p>
                    <strong>
                      All refunds are processed to your original payment method:
                    </strong>
                  </p>
                  <ul className="grid gap-3 list-disc pl-4">
                    <li>
                      <strong>Credit/Debit Cards:</strong> Refunds appear as a
                      credit on your statement within 5-10 business days
                    </li>
                    <li>
                      <strong>PayPal:</strong> Refunds are typically processed
                      instantly to your PayPal account
                    </li>
                    <li>
                      <strong>Bank Transfers:</strong> May take 7-14 business
                      days depending on your bank
                    </li>
                    <li>
                      <strong>Digital Wallets:</strong> Processing times vary by
                      provider (Apple Pay, Google Pay, etc.)
                    </li>
                  </ul>
                  <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900 rounded-lg p-4 mt-4">
                    <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                      <strong>Important:</strong> We cannot process refunds to a
                      different payment method than the one used for the
                      original purchase due to security and fraud prevention
                      policies.
                    </p>
                  </div>
                </div>
              </section>

              {/* Abuse Prevention */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    7. Refund Abuse Prevention
                  </h2>
                </div>
                <div className="space-y-3 text-gray-700 dark:text-gray-300 leading-relaxed ml-11">
                  <p>
                    To maintain fair pricing for all customers, we monitor
                    refund patterns and reserve the right to:
                  </p>
                  <ul className="grid gap-3 list-disc pl-4">
                    <li>
                      Refuse refunds for accounts with excessive refund requests
                    </li>
                    <li>
                      Investigate suspicious refund patterns or potential abuse
                    </li>
                    <li>
                      Terminate accounts that violate our Terms of Service
                    </li>
                    <li>Report fraudulent activity to relevant authorities</li>
                  </ul>
                  <p>
                    We appreciate your understanding in helping us maintain a
                    sustainable service for all users.
                  </p>
                </div>
              </section>
            </div>

            <Separator className="bg-gray-100 dark:bg-neutral-800" />

            {/* Contact Us */}
            <section className="text-center bg-gray-50 dark:bg-neutral-800/50 rounded-2xl p-8 border border-gray-100 dark:border-neutral-800">
                <div className="flex flex-col items-center gap-3 mb-4">
                    <div className="p-3 bg-white dark:bg-neutral-800 rounded-full shadow-sm">
                        <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t.legal.haveQuestions}</h3>
                </div>
              <p className="text-gray-600 dark:text-gray-400">
                {t.legal.contactEmail}{' '}
                <a
                  href="mailto:support@zapify.pro"
                  className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                >
                  support@zapify.pro
                </a>
                .
              </p>
            </section>

            {/* Back to top */}
            <div className="text-center pt-8">
              <Link to="/">
                <Button size="lg" variant="gradient-brand">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t.legal.returnToWebsite}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
